import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded"
import HourglassTopRoundedIcon from "@mui/icons-material/HourglassTopRounded"
import { Stack, Typography } from "@mui/material"
import { ReactElement } from "react"

import DashboardPanelFrame from "../../components/dashboard/dashboardPanelFrame"
import ListDevices from "../../components/dashboard/listDevices"
import SingleKpiCard from "../../components/dashboard/singleKpiCard"
import ErrorDisplay from "../../components/fallback/errorDisplay"
import DashboardHomepageSkeleton from "../../components/loading/skeletons/dashboardHomepageSkeleton"
import { DISPLAY_ROLES, ENA_SYS_ADMIN } from "../../constants/roles"
import {
    TT_DEVICES_ALERT,
    TT_DEVICES_APPROVED,
    TT_DEVICES_AWAITING_ASSESSMENT,
} from "../../constants/tooltips"
import { useGetUserData } from "../../hooks/useGetUserData"
import { AWAITING_ASSESSMENT_DEVICES } from "../../router/routes"
import { useGetSysAdminHomepageData } from "../../services/middleware/hooks/sysAdminInterface/useGetSysAdminHomepageData"
import { GRID_SPACING } from "../../themes/defaultThemeParams"

/**
 * Renders the dashboard for SysAdmin users which is displayed as their homepage
 *
 */
const SysAdminHomepage = (): ReactElement => {
    const { firstName } = useGetUserData()
    const { data, error, isLoading } = useGetSysAdminHomepageData()

    if (isLoading) {
        return <DashboardHomepageSkeleton />
    }
    if (error) {
        return <ErrorDisplay error={error} />
    }
    if (!data) {
        return <Typography> KPI Currently Unavailable</Typography>
    }

    return (
        <Stack
            direction={{ xs: "column", md: "column" }}
            spacing={GRID_SPACING}
            alignItems="stretch"
        >
            <DashboardPanelFrame
                userName={firstName}
                permission={DISPLAY_ROLES[ENA_SYS_ADMIN]}
            />

            <Stack
                direction={{ xs: "column", md: "row" }}
                gap={GRID_SPACING}
                justifyContent="center"
                marginBottom={GRID_SPACING}
            >
                <SingleKpiCard
                    Icon={HourglassTopRoundedIcon}
                    value={data.totDevicesAwaitingAssessment}
                    title="Devices Awaiting Assessment"
                    link={AWAITING_ASSESSMENT_DEVICES}
                />
                <SingleKpiCard
                    Icon={ErrorRoundedIcon}
                    value={data.totLiveAlerts}
                    title="Devices with Alerts"
                />
            </Stack>

            <Stack
                direction={{ xs: "column", md: "row" }}
                gap={GRID_SPACING}
                justifyContent="center"
                marginBottom={GRID_SPACING}
            >
                <ListDevices
                    cardTitle="Devices Awaiting Assessment"
                    cardDescription="These are the latest 20 devices across both demand and generation that are awaiting assessment"
                    cardTooltip={TT_DEVICES_AWAITING_ASSESSMENT}
                    data={data.topAwaitingAssessmentSummary}
                />

                <ListDevices
                    cardTitle="Approved Devices"
                    cardDescription="These are the latest 20 devices across both demand and generation that have been approved"
                    cardTooltip={TT_DEVICES_APPROVED}
                    data={data.topCompliantSummary}
                />
            </Stack>

            <Stack
                direction={{ xs: "column", md: "row" }}
                gap={GRID_SPACING}
                // remove paddingRight when adding a new card
                paddingRight={{ lg: 3 }}
                marginBottom={GRID_SPACING}
            >
                <ListDevices
                    cardTitle="Devices with Alerts"
                    cardDescription="These are the latest 20 devices across both demand and generation that have had alerts raised"
                    cardTooltip={TT_DEVICES_ALERT}
                    data={data.topLiveAlerts.map((alert) => ({
                        ...alert,
                        systemReference: alert.deviceSystemRef,
                    }))}
                />
            </Stack>
        </Stack>
    )
}

export default SysAdminHomepage
